import { useEffect, useMemo, useState } from "react";

const EASTERN_TIMEZONE = "America/New_York";
const CLASS_START_HOUR = 20;
const CLASS_DURATION_MS = 60 * 60 * 1000;

const getZonedParts = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  return parts.reduce((values, part) => {
    if (part.type !== "literal") values[part.type] = Number(part.value);
    return values;
  }, {});
};

const getTimeZoneOffset = (date, timeZone) => {
  const parts = getZonedParts(date, timeZone);
  const zonedAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return zonedAsUtc - Math.floor(date.getTime() / 1000) * 1000;
};

const zonedDateTimeToUtc = (values, timeZone) => {
  const utcGuess = Date.UTC(
    values.year,
    values.month - 1,
    values.day,
    values.hour,
    values.minute || 0,
    values.second || 0
  );
  const firstOffset = getTimeZoneOffset(new Date(utcGuess), timeZone);
  const firstResult = new Date(utcGuess - firstOffset);
  const finalOffset = getTimeZoneOffset(firstResult, timeZone);
  return new Date(utcGuess - finalOffset);
};

const addDays = (parts, days) => {
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  date.setUTCDate(date.getUTCDate() + days);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
};

export const getClassSchedule = (nowValue = new Date()) => {
  const now = nowValue instanceof Date ? nowValue : new Date(nowValue);
  const easternNow = getZonedParts(now, EASTERN_TIMEZONE);
  const easternDate = new Date(Date.UTC(easternNow.year, easternNow.month - 1, easternNow.day));
  const dayOfWeek = easternDate.getUTCDay();
  const daysUntilWednesday = (3 - dayOfWeek + 7) % 7;
  let classDate = addDays(easternNow, daysUntilWednesday);
  let classStart = zonedDateTimeToUtc(
    { ...classDate, hour: CLASS_START_HOUR },
    EASTERN_TIMEZONE
  );
  let classEnd = new Date(classStart.getTime() + CLASS_DURATION_MS);

  if (now.getTime() >= classEnd.getTime()) {
    classDate = addDays(classDate, 7);
    classStart = zonedDateTimeToUtc(
      { ...classDate, hour: CLASS_START_HOUR },
      EASTERN_TIMEZONE
    );
    classEnd = new Date(classStart.getTime() + CLASS_DURATION_MS);
  }

  const isLive = now.getTime() >= classStart.getTime() && now.getTime() < classEnd.getTime();
  const countdownTarget = isLive ? classEnd : classStart;

  return {
    isLive,
    classStart,
    classEnd,
    countdownTarget,
    remainingMs: Math.max(0, countdownTarget.getTime() - now.getTime()),
  };
};

export const useClassSchedule = () => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  return useMemo(() => getClassSchedule(now), [now]);
};